import { Formik, Form, Field } from 'formik'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { CLOSE, SECONDARY } from 'app/constants/button'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import { changePasswordSchemaValidation } from 'commons/schemaValidation'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { useState } from 'react'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import ContainedButton from 'app/componentV4/Button/ContainedButton'

const ChangePasswordDialogFormik = ({
  isOpen,
  isPending,
  onCancel,
  onValidSubmit,
}) => {
  const { formatMessage } = useIntl()
  const [isShowCurrentPassword, setIsShowCurrentPassword] = useState(false)
  const [isShowNewPassword, setIsShowNewPassword] = useState(false)
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false)

  return (
    <ResponsiveDialog
      open={isOpen}
      isFullScreen={false}
      scroll="paper"
      maxWidth="xs"
    >
      <DialogTitle>
        <FormattedMessage id="userMenu.changePassword.label" />
      </DialogTitle>

      <Formik
        initialValues={{
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }}
        validationSchema={changePasswordSchemaValidation(formatMessage)}
        onSubmit={(values, actions) => {
          onValidSubmit(values)
          actions.setSubmitting(false)
        }}
      >
        {({ values, touched, errors, isValid, isSubmitting }) => (
          <Form className="flex flex-col">
            <DialogContent>
              <div className="space-y-5">
                <Field name="currentPassword">
                  {({ field }) => (
                    <LabelInput
                      label={formatMessage({
                        id: 'changePassword.placeholder.currentPassword',
                      })}
                      required
                      className="w-full mb-0"
                    >
                      <SimpleInput
                        {...field}
                        type={isShowCurrentPassword ? 'text' : 'password'}
                        id="current-password-input"
                        name="currentPassword"
                        placeholder={formatMessage({
                          id: 'changePassword.placeholder.currentPassword',
                        })}
                        value={values.currentPassword}
                        size="medium"
                        fullWidth
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              tabIndex={-1}
                              aria-label="toggle current password visibility"
                              onClick={() =>
                                setIsShowCurrentPassword(!isShowCurrentPassword)
                              }
                            >
                              {isShowCurrentPassword ? (
                                <Visibility
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              ) : (
                                <VisibilityOff
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        error={
                          touched.currentPassword &&
                          Boolean(errors.currentPassword)
                        }
                        helperText={
                          touched.currentPassword && errors.currentPassword
                        }
                      />
                    </LabelInput>
                  )}
                </Field>
                <Field name="newPassword">
                  {({ field }) => (
                    <LabelInput
                      label={formatMessage({
                        id: 'changePassword.placeholder.newPassword',
                      })}
                      required
                      className="w-full mb-0"
                    >
                      <SimpleInput
                        {...field}
                        type={isShowNewPassword ? 'text' : 'password'}
                        id="new-password-input"
                        name="newPassword"
                        placeholder={formatMessage({
                          id: 'changePassword.placeholder.newPassword',
                        })}
                        value={values.newPassword}
                        size="medium"
                        // onChange={handleChange}
                        // onBlur={handleBlur}
                        fullWidth
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              tabIndex={-1}
                              aria-label="toggle new password visibility"
                              onClick={() =>
                                setIsShowNewPassword(!isShowNewPassword)
                              }
                            >
                              {isShowNewPassword ? (
                                <Visibility
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              ) : (
                                <VisibilityOff
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        error={
                          touched.newPassword && Boolean(errors.newPassword)
                        }
                        helperText={touched.newPassword && errors.newPassword}
                      />
                    </LabelInput>
                  )}
                </Field>
                <Field name="confirmNewPassword">
                  {({ field }) => (
                    <LabelInput
                      label={formatMessage({
                        id: 'registerForm.confirmPasswordPlaceholder',
                      })}
                      required
                      className="w-full mb-0"
                    >
                      <SimpleInput
                        {...field}
                        type={isShowConfirmPassword ? 'text' : 'password'}
                        id="confirm-password-input"
                        name="confirmPassword"
                        placeholder={formatMessage({
                          id: 'registerForm.confirmPasswordPlaceholder',
                        })}
                        value={values.confirmPassword}
                        size="medium"
                        fullWidth
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              tabIndex={-1}
                              aria-label="toggle new password visibility"
                              onClick={() =>
                                setIsShowConfirmPassword(!isShowConfirmPassword)
                              }
                            >
                              {isShowConfirmPassword ? (
                                <Visibility
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              ) : (
                                <VisibilityOff
                                  fontSize="small"
                                  className="text-grey-200"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                      />
                    </LabelInput>
                  )}
                </Field>
              </div>
            </DialogContent>

            <DialogActions className="mb-10">
              <div className="w-full flex justify-center space-x-5">
                <OutlinedButtons
                  tabIndex={-1}
                  id="close-change-password-dialog"
                  type="button"
                  color={CLOSE}
                  disabled={isSubmitting || isPending}
                  onClick={onCancel}
                >
                  <FormattedMessage id="text.close" />
                </OutlinedButtons>
                <ContainedButton
                  id="save-change-password-btn"
                  color={SECONDARY}
                  isLoading={isSubmitting || isPending}
                  type="submit"
                  disabled={isSubmitting || !isValid || isPending}
                >
                  <FormattedMessage id="action.save" />
                </ContainedButton>
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </ResponsiveDialog>
  )
}

export default ChangePasswordDialogFormik
